










































































































































































































































































































import axios from 'axios';
import moment from 'moment-timezone';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

import { BaseVue } from '@/BaseVue';
import CreateInventoryView from '@/components/inventory/CreateInventoryView.vue';
import ReportHeader from '@/components/inventory/ReportHeader.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiCheckbox from '@/components/ui/UiCheckbox.vue';
import UiDataTable from '@/components/ui/UiDataTable.vue';
import UiDatePicker from '@/components/ui/UiDatePicker.vue';
import UiTruncateText from '@/components/ui/UiTruncateText.vue';
import WalletListNew from '@/components/wallets/WalletListNew.vue';
import { Subsidiary } from '@/types/inventories';
import { getClusterAwareBaseUrl } from '@/utils/clusterAwareUrl';
import { clearDownloadIframes, downloadAuthorizedFile } from '@/utils/downloadFile';
import numberUtils from '@/utils/numberUtils';

import { baConfig } from '../../../../config';
import {
  ApiSvcInventoryAction,
  ApiSvcInventoryCounts,
  ApiSvcInventoryView,
  ApiSvcTaxStrategyType,
  InventoryApi,
} from '../../../../generated/api-svc';

@Component({
  components: {
    UiCheckbox,
    CreateInventoryView,
    UiDatePicker,
    UiButton,
    WalletListNew,
    UiDataTable,
    UiTruncateText,
    ReportHeader,
  },
})
export default class InventoryViewActions extends BaseVue {
  @Prop({ default: null })
  public readonly view!: ApiSvcInventoryView | null;

  @Prop({ default: null })
  public readonly defaultFilter!: { [id: string]: Array<string | number> };

  @Prop({ default: '' })
  public readonly defaultAsOf!: string;

  public actions: ApiSvcInventoryAction[] = [];
  public asOf = moment.tz(moment.tz.guess()).subtract(1, 'day').format('YYYY-MM-DD');
  public startDate = '';
  public nextPageToken?: string = '';
  public numFormat = numberUtils.getFormatter({ accountingNegative: true });
  public submittedAsOf = '';
  public includeSubsidiary = false;

  public get isLoading() {
    // Only block UI for main data loading, not for filter column loading (handled per-column now)
    return this.isLotsLoading || this.isCountsLoading;
  }

  public get headers() {
    const headers = [
      {
        id: 'subsidiary',
        label: 'Subsidiary',
        defaultVisibility: this.includeSubsidiary && this.showSubsidiary,
        sortable: false,
        filterable: true,
        isPaginated: true,
      },
      {
        id: 'timestampSEC',
        label: 'Timestamp',
        defaultVisibility: true,
        sortable: true,
      },
      {
        id: 'action',
        label: 'Action',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      },
      {
        id: 'status',
        label: 'Status',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      },
    ];
    if (this.view?.inventoryConfig?.inventoryMappingRule?.type === 'inventory-per-wallet') {
      headers.push({
        id: 'wallet',
        label: 'Wallet',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      });
      headers.push({
        id: 'inventory',
        label: 'Inventory',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      });
      headers.push({
        id: 'toInventory',
        label: 'To Inventory',
        defaultVisibility: true,
        sortable: true,
      });
      headers.push({
        id: 'fromInventory',
        label: 'From Inventory',
        defaultVisibility: true,
        sortable: true,
      });
      headers.push({
        id: 'originalLotId',
        label: 'Original Lot ID',
        defaultVisibility: false,
        sortable: true,
      });
      headers.push({
        id: 'transferLotId',
        label: 'Transfer Lot ID',
        defaultVisibility: false,
        sortable: true,
      });
    } else if (this.view?.inventoryConfig?.inventoryMappingRule?.type === 'inventory-group-mapping') {
      headers.push({
        id: 'wallet',
        label: 'Wallet',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      });
      headers.push({
        id: 'inventory',
        label: 'Inventory',
        defaultVisibility: true,
        sortable: true,
        filterable: true,
        isPaginated: true,
      });
    }
    headers.push(
      ...[
        {
          id: 'txnId',
          label: 'Txn ID',
          defaultVisibility: true,
          sortable: true,
        },
        {
          id: 'lotId',
          label: 'Lot ID',
          defaultVisibility: false,
          sortable: true,
        },
        {
          id: 'asset',
          label: 'Asset',
          defaultVisibility: true,
          sortable: true,
          filterable: true,
          isPaginated: true,
        },
        {
          id: 'assetUnitAdj',
          label: 'Asset Unit Adj',
          defaultVisibility: true,
          sortable: true,
        },
        {
          id: 'assetBalance',
          label: 'Asset Balance',
          defaultVisibility: true,
          sortable: true,
        },
        {
          id: 'txnExchangeRate',
          label: 'Txn Exchange Rate',
          defaultVisibility: true,
          sortable: true,
        },
        {
          id: 'carryingValue',
          label: 'Carrying Value',
          defaultVisibility: true,
          sortable: true,
        },
      ]
    );
    if (this.view?.impair) {
      headers.push({
        id: 'impairmentExpense',
        label: 'Impairment Expense',
        defaultVisibility: true,
        sortable: true,
      });
    }
    headers.push(
      ...[
        {
          id: 'proceeds',
          label: 'Proceeds',
          defaultVisibility: false,
          sortable: true,
        },
      ]
    );
    if (!this.isAverageCost) {
      headers.push(
        ...[
          {
            id: 'shortTermGainLoss',
            label: 'Short Term Gain Loss',
            defaultVisibility: false,
            sortable: true,
          },
          {
            id: 'longTermGainLoss',
            label: 'Long Term Gain Loss',
            defaultVisibility: false,
            sortable: true,
          },
        ]
      );
    }
    if (this.isAverageCost) {
      headers.push({
        id: 'undatedGainLoss',
        label: 'Undated Gain Loss',
        defaultVisibility: false,
        sortable: true,
      });
    }
    headers.push(
      ...[
        {
          id: 'costBasisAcquired',
          label: 'Cost Basis Acquired',
          defaultVisibility: false,
          sortable: true,
        },
        {
          id: 'costBasisRelieved',
          label: 'Cost Basis Relieved',
          defaultVisibility: true,
          sortable: true,
        },
      ]
    );
    if (this.isAverageCost) {
      headers.push({
        id: 'costAverageRate',
        label: 'Cost Average Rate',
        defaultVisibility: false,
        sortable: true,
      });
    }
    headers.push(
      ...[
        {
          id: 'isTrade',
          label: 'Is Trade',
          defaultVisibility: false,
          sortable: true,
        },
        {
          id: 'lineError',
          label: 'Line Error',
          defaultVisibility: false,
          sortable: true, // line error only true if there is an error
        },
      ]
    );

    return headers;
  }

  public get isAverageCost() {
    return this.view?.inventoryPickingStrategy.type === ApiSvcTaxStrategyType.NUMBER_4;
  }

  public sort: any = {};
  public filters: any = {};
  public uniqueColumnValues: { [id: string]: Array<string | number> } = {};
  public counts: ApiSvcInventoryCounts | null = null;

  public setFilter(options: { [id: string]: Array<string | number> }) {
    Vue.set(this.$refs.actionsTable as Vue, 'selectedFilters', options);
    this.onFilter(options);
  }

  public viewActions(options: { status: string; asOf: string }) {
    this.setFilter({ status: [options.status] });
  }

  public onFilter(filters: any) {
    this.filters = filters;
    this.loadActions();
  }

  public onSort(sort: any) {
    this.sort = sort;
    this.loadActions();
  }

  public isLotsLoading = false;
  public isCountsLoading = false;
  public loadingNextPage = false;
  public columnLoadingState: { [columnId: string]: boolean } = {};

  async loadColumnUniqueValuesWithPagination(columnId: string): Promise<Array<string | number>> {
    this.$set(this.columnLoadingState, columnId, true);
    const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
    let allValues: Array<string | number> = [];
    let pageToken: string | undefined;
    let hasMore = true;

    try {
      while (hasMore && this.view?.id) {
        try {
          const resp = await svc.getActionColumnUniqueValues(
            this.orgId,
            this.view.id,
            columnId,
            false,
            this.submittedAsOf,
            pageToken,
            this.startDate, // Added startDate parameter for date range filtering
            {
              withCredentials: true,
            }
          );

          if (resp.status === 200 && resp.data.values) {
            allValues = allValues.concat(resp.data.values as Array<string | number>);
            pageToken = resp.data.nextPageToken;
            hasMore = !!pageToken;
          } else {
            hasMore = false;
          }
        } catch (error) {
          console.error(`Error loading unique values for ${columnId}:`, error);
          hasMore = false;
        }
      }
    } finally {
      this.$set(this.columnLoadingState, columnId, false);
    }

    return allValues;
  }

  async loadUniqueColumns() {
    if (this.view && this.view.id) {
      // Load all columns in parallel, each managing its own loading state
      const [actionValues, assetValues, statusValues, inventoryValues, walletValues] = await Promise.all([
        this.loadColumnUniqueValuesWithPagination('action'),
        this.loadColumnUniqueValuesWithPagination('asset'),
        this.loadColumnUniqueValuesWithPagination('status'),
        this.loadColumnUniqueValuesWithPagination('inventory'),
        this.loadColumnUniqueValuesWithPagination('wallet'),
      ]);

      this.$set(this.uniqueColumnValues, 'action', actionValues);
      this.$set(this.uniqueColumnValues, 'asset', assetValues);
      this.$set(this.uniqueColumnValues, 'status', statusValues);
      this.$set(this.uniqueColumnValues, 'inventory', inventoryValues);
      this.$set(this.uniqueColumnValues, 'wallet', walletValues);

      if (this.includeSubsidiary && this.showSubsidiary) {
        this.$set(
          this.uniqueColumnValues,
          'subsidiary',
          this.$store.state.subsidiaries?.subsidiaries.map((s: Subsidiary) => ({ id: s.id, label: s.name }))
        );
      }
    }
  }

  async loadUniqueColumnsPaginated(column: string) {
    if (this.view && this.view.id) {
      let nextPageToken: string | undefined;
      let hasMore = true;

      const columnData: Array<string | number> = [];

      while (hasMore) {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        const promise = svc.getActionColumnUniqueValues(
          this.orgId,
          this.view.id,
          column,
          false,
          this.submittedAsOf,
          nextPageToken,
          this.startDate,
          {
            withCredentials: true,
          }
        );

        const resp = await promise;

        if (resp.status === 200) {
          columnData.push(...(resp.data.values as Array<string | number>));
          nextPageToken = resp.data.nextPageToken;
          hasMore = nextPageToken !== undefined;
        }
      }

      this.$set(this.uniqueColumnValues, column, columnData);
    }
  }

  async loadActions(getNextPage?: boolean) {
    if (this.view && this.view.id) {
      if (getNextPage) {
        this.loadingNextPage = true;
      } else {
        this.isLotsLoading = true;
      }

      try {
        const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
        const promise = svc.getViewActions(
          this.orgId,
          this.view.id,
          this.submittedAsOf,
          getNextPage === true ? this.nextPageToken : undefined,
          undefined,
          undefined,
          this.filters.action ?? undefined,
          this.filters.status ?? undefined,
          undefined,
          undefined,
          undefined,
          this.filters.asset ?? undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          this.sort.id ?? undefined,
          this.sort.asc ? 'ASC' : 'DESC',
          false,
          this.filters.inventory ?? undefined,
          this.filters.wallet ?? undefined,
          this.filters.subsidiary ?? undefined,
          this.startDate,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          {
            withCredentials: true,
          }
        );

        const resp = await promise;

        if (resp.status === 200) {
          // map subsidiary name
          if (this.includeSubsidiary && this.showSubsidiary) {
            resp.data.actions.map((line: any) => {
              const subObj = (this.$store.state.subsidiaries.subsidiaries as Subsidiary[]).find(
                (subs) => subs?.id === line?.subsidiaryId
              );
              Object.assign(line, { subsidiary: subObj?.name ?? '-' });
              return line;
            });
          }

          if (getNextPage) {
            this.actions.push(...resp.data.actions);
          } else {
            this.actions = resp.data.actions;
          }

          this.nextPageToken = resp.data.nextPageToken ?? '';
        }
      } finally {
        if (getNextPage) {
          this.loadingNextPage = false;
        } else {
          this.isLotsLoading = false;
        }
      }
    }
  }

  async loadCount() {
    this.isCountsLoading = true;
    if (this.view && this.view.id) {
      const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
      const promise = svc.getViewCounts(this.orgId, this.view.id, this.submittedAsOf, this.startDate, undefined, {
        withCredentials: true,
      });
      const resp = await promise;
      if (resp.status === 200) {
        this.counts = resp.data.counts;
      }
      this.isCountsLoading = false;
    }
  }

  public baseUrl = getClusterAwareBaseUrl(process.env.VUE_APP_RPT_API_URL ?? process.env.VUE_APP_API_URL!);
  public csvLoading = false;

  downloadFile = (link: string) => downloadAuthorizedFile(link);

  public async downloadCSV() {
    if (!this.view?.id) return;
    this.csvLoading = true;
    try {
      const svc = new InventoryApi(undefined, baConfig.getFriendlyApiUrl());
      const resp = await svc.getViewActions(
        this.orgId,
        this.view.id,
        this.submittedAsOf,
        undefined,
        undefined,
        undefined,
        this.filters.action ?? undefined,
        this.filters.status ?? undefined,
        undefined,
        undefined,
        undefined,
        this.filters.asset ?? undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        this.sort.id ?? undefined,
        this.sort.asc ? 'ASC' : 'DESC',
        true,
        this.filters.inventory ?? undefined,
        this.filters.wallet ?? undefined,
        this.filters.subsidiary ?? undefined,
        this.startDate,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
          withCredentials: true,
        }
      );

      if (resp.status === 200) {
        const exportIds = resp.data.exportIds;
        const downloadUrlPromises = [] as any[];
        exportIds?.forEach((x: any) => {
          downloadUrlPromises.push(
            axios.get(`${this.baseUrl}v2/orgs/${this.$store.state.currentOrg.id}/exports/${x}?rawUrl=true`, {
              withCredentials: true,
            })
          );
        });
        clearDownloadIframes();
        const downloadUrls = await Promise.all(downloadUrlPromises);
        downloadUrls.forEach((x: any) => {
          this.downloadFile(x.data);
        });
      }
    } finally {
      this.csvLoading = false;
    }
  }

  public loadData() {
    this.submittedAsOf = this.asOf;
    this.loadActions();
    this.loadUniqueColumns();
    this.loadCount();
  }

  async mounted() {
    const queryParams = this.$route.query;
    this.asOf = queryParams.asOf && typeof queryParams.asOf === 'string' ? queryParams.asOf : this.asOf;

    if (this.defaultFilter) {
      this.setFilter(this.defaultFilter);
    }
    if (this.defaultAsOf) {
      this.asOf = this.defaultAsOf;
    }
    this.loadData();
  }

  async getNextPage() {
    if (this.nextPageToken) {
      await this.loadActions(true);
    }
  }

  public get showSubsidiary() {
    return (
      this.checkFeatureFlag('subsidiaries') &&
      this.$store.state.subsidiaries?.subsidiaries.length > 0 &&
      this.view?.inventoryConfig?.inventoryMappingRule?.type === 'inventory-per-wallet'
    );
  }

  public onIncludeSubsidiaryChange() {
    this.loadData();
  }
}
